import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Coins, CreditCard, Apple } from "lucide-react";
import { toast } from "sonner";

export default function Pricing({ onOpenAuth }) {
  const { user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(null);
  const [meta, setMeta] = useState({});

  useEffect(() => {
    http.get("/payments/packages").then((r) => {
      setPacks(r.data.packs);
      setMeta({ base: r.data.base_cost, extras: r.data.premium_extras, bonus: r.data.signup_bonus });
    });
  }, []);

  const buy = async (lookup_key) => {
    if (!user) { onOpenAuth("register"); return; }
    setBusy(lookup_key);
    try {
      const r = await http.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-20" data-testid="pricing-page">
      <div className="max-w-2xl">
        <p className="text-xs font-mono uppercase tracking-widest text-emerald-800">Credits</p>
        <h1 className="mt-2 font-display text-3xl lg:text-5xl font-bold text-slate-900">
          Pay only for what you build.
        </h1>
        <p className="mt-3 text-slate-600 text-lg">
          Every message to the AI builder costs credits. One-time top-ups. Credits never expire.
        </p>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800">
          <Sparkles className="h-3.5 w-3.5" />
          New accounts get {meta.bonus} free credits · try before you buy
        </div>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="pricing-grid">
        {packs.map((p) => {
          const highlight = p.badge === "Most popular";
          return (
            <div
              key={p.lookup_key}
              data-testid={`pack-${p.lookup_key}`}
              className={`relative rounded-2xl p-8 flex flex-col ${
                highlight
                  ? "border-2 border-emerald-700 bg-gradient-to-br from-emerald-50 to-white shadow-lg"
                  : "border border-slate-200 bg-white"
              }`}
            >
              {p.badge && (
                <div className="absolute -top-3 left-8 rounded-full bg-emerald-900 text-white text-[11px] font-semibold uppercase tracking-wider px-3 py-1">
                  {p.badge}
                </div>
              )}
              <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
              <p className="mt-1 text-sm text-slate-600">{p.tagline}</p>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-display text-5xl font-extrabold text-slate-900">
                  ${(p.price_cents / 100).toFixed(0)}
                </span>
                <span className="text-slate-500">one-time</span>
              </div>
              <div className="mt-3 flex items-center gap-2 text-emerald-800">
                <Coins className="h-4 w-4" />
                <span className="font-semibold text-lg">{p.credits.toLocaleString()} credits</span>
              </div>
              <ul className="mt-6 space-y-2 text-sm text-slate-600 flex-1">
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-700 shrink-0 mt-0.5" />All three frontier models</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-700 shrink-0 mt-0.5" />Credits never expire</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-700 shrink-0 mt-0.5" />Unlimited projects & templates</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-700 shrink-0 mt-0.5" />HIPAA-aware scaffolds</li>
              </ul>
              <Button
                onClick={() => buy(p.lookup_key)}
                disabled={busy === p.lookup_key}
                data-testid={`btn-buy-${p.lookup_key}`}
                className={`mt-8 h-11 ${
                  highlight
                    ? "bg-emerald-900 hover:bg-emerald-800 text-white"
                    : "bg-slate-900 hover:bg-slate-800 text-white"
                }`}
              >
                {busy === p.lookup_key ? "Redirecting..." : `Buy ${p.credits.toLocaleString()} credits`}
              </Button>
              <div className="mt-3 flex items-center justify-center gap-3 text-[11px] text-slate-500">
                <span className="flex items-center gap-1"><CreditCard className="h-3 w-3" />Card</span>
                <span className="flex items-center gap-1"><Apple className="h-3 w-3" />Apple Pay</span>
                <span>· G Pay</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-12 rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-display font-semibold text-slate-900">How credits work</h2>
        <div className="mt-3 grid sm:grid-cols-3 gap-4 text-sm text-slate-600">
          <div>
            <div className="text-xs font-mono uppercase text-emerald-800">Standard message</div>
            <div className="mt-1"><b>{meta.base ?? 1} credit</b> · GPT 5.6 Terra</div>
          </div>
          <div>
            <div className="text-xs font-mono uppercase text-emerald-800">Premium model</div>
            <div className="mt-1"><b>{(meta.base ?? 1) + 2} credits</b> · Claude Sonnet 5 / Gemini 3.1 Pro</div>
          </div>
          <div>
            <div className="text-xs font-mono uppercase text-emerald-800">Payments</div>
            <div className="mt-1">Secure Stripe Checkout · card, Apple Pay & Google Pay</div>
          </div>
        </div>
      </div>
    </div>
  );
}
